'use client'

import * as React from 'react';
import {Fragment, useState} from 'react';
import {FooterGroup, FooterLinkWithLinkAndLabel} from '@/components/footer/FooterGroup';
import {hasLinkAndLabel} from '@/lib/type-guards';
import {
  Accordion,
  accordionClasses,
  AccordionDetails,
  accordionDetailsClasses,
  AccordionSummary,
  Box,
  Container,
  Fade,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  StyledFooterCopyRightContainer, StyledFooterGroupContainer, StyledFooterScrollUpContainer,
  StyledFooterBottomContentContainer,
  StyledFooterDesktopGroupContainer, StyledSmScreenCopyRightContainer,
  StyledSmScreenFooterContainer, StyledFooterContainer
} from '@/components/footer/Footer.styles';
import {
  FooterColumnFieldsFragment,
  FooterGroupFieldsFragment,
  FooterLinkFieldsFragment
} from '@generated/graphql';
import {ArrowUpIcon} from '@/components/ui/icons/ArrowUp';
import {useTranslations} from 'next-intl';
import {FooterInternalLink} from '@/components/footer/FooterInternalLink';
import tv4Theme from '@/theme/tv4Theme';
import {useReducedMotion, scrollBehavior} from '@/utils/useReducedMotion';

const CURRENT_YEAR = new Date().getFullYear();

const accordionBaseSx = {
  backgroundColor: 'transparent',
  '&:before': { display: 'none' },
};

type FooterProps = {
  columns: FooterColumnFieldsFragment[];
  copyRightLinks: FooterLinkFieldsFragment[]
};

type FooterWithSafeLinksProps = {
  columns: FooterColumnFieldsFragment[];
  safeCopyRightLinks: FooterLinkWithLinkAndLabel[]
};

function DesktopColumns({ columns, safeCopyRightLinks }: FooterWithSafeLinksProps) {
  const t = useTranslations('Footer');
  const reducedMotion = useReducedMotion();
  return (
    <Box>
      <StyledFooterDesktopGroupContainer>
        {columns.map((col, idx) => {
          const groups = col.footerGroupsCollection?.items ?? [];
          const validGroups = groups.filter((g): g is FooterGroupFieldsFragment => g != null);

          return (
            <StyledFooterGroupContainer key={idx}>
              {validGroups.map((group) => (
                <FooterGroup withLabel group={group} key={group.sys.id} />
              ))}
            </StyledFooterGroupContainer>
          );
        })}
      </StyledFooterDesktopGroupContainer>
      <StyledFooterBottomContentContainer>
        <FooterCopyRight safeCopyRightLinks={safeCopyRightLinks} />
        <StyledFooterScrollUpContainer onClick={() => window.scrollTo({ top: 0, behavior: scrollBehavior(reducedMotion) })}>
          <Typography variant='caption' align='center' sx={(theme) => ({ color: theme.palette.text.primary })}>
            {t('scrollUp')}
          </Typography>
          <ArrowUpIcon size={10} />
        </StyledFooterScrollUpContainer>
      </StyledFooterBottomContentContainer>
    </Box>
  );
}

function MobileAccordions({ groups, safeCopyRightLinks }: { groups: FooterGroupFieldsFragment[], safeCopyRightLinks: FooterLinkWithLinkAndLabel[]  }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const handleExpansion = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <StyledSmScreenFooterContainer>
      {groups.map((group) => (
        <Accordion
          key={group.sys.id}
          disableGutters
          elevation={0}
          slots={{ transition: Fade }}
          slotProps={{ transition: { timeout: 400 } }}
          sx={[
            accordionBaseSx,
            expanded[group.sys.id]
              ? {
                [`& .${accordionClasses.region}`]: { height: 'auto' },
                [`& .${accordionDetailsClasses.root}`]: { display: 'block' },
              }
              : {
                [`& .${accordionClasses.region}`]: { height: 0 },
                [`& .${accordionDetailsClasses.root}`]: { display: 'none' },
              },
          ]}
          onChange={() => handleExpansion(group.sys.id)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography component='p' variant='subtitle1'>{group.label}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ marginLeft: 2.5}}>
            <FooterGroup group={group} />
          </AccordionDetails>
        </Accordion>
      ))}
      <StyledSmScreenCopyRightContainer>
        <FooterCopyRight safeCopyRightLinks={safeCopyRightLinks} />
      </StyledSmScreenCopyRightContainer>
    </StyledSmScreenFooterContainer>
  );
}

export default function Footer({ columns, copyRightLinks }: FooterProps) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('sm'));

  const allGroups: FooterGroupFieldsFragment[] = React.useMemo(() => {
    const groupsAcc: FooterGroupFieldsFragment[] = [];
    columns.forEach((col) => {
      const items = col.footerGroupsCollection?.items ?? [];
      items.forEach((g) => {
        if (g) groupsAcc.push(g);
      });
    });
    return groupsAcc;
  }, [columns]);

  const safeCopyRightLinks = copyRightLinks.filter(hasLinkAndLabel);

  return (
    <StyledFooterContainer>
      <Container maxWidth='xl'>
        {isMdUp ? <DesktopColumns columns={columns} safeCopyRightLinks={safeCopyRightLinks} /> : <MobileAccordions safeCopyRightLinks={safeCopyRightLinks} groups={allGroups} />}
      </Container>
    </StyledFooterContainer>
  );
}

type FooterCopyRightProps = {
  safeCopyRightLinks: FooterLinkWithLinkAndLabel[];
};

function FooterCopyRight({ safeCopyRightLinks }: FooterCopyRightProps) {
  return (
    <StyledFooterCopyRightContainer>
      <Typography variant='caption' sx={(theme) => ({ color: theme.palette.text.primary })}>
          © {CURRENT_YEAR} TV4 | Org. nr. 556242-7152
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {safeCopyRightLinks.map((link, i) => (
          <Fragment key={link.sys.id}>
            <FooterInternalLink link={link} fontSize={tv4Theme.typography.caption.fontSize} />
            {i < safeCopyRightLinks.length - 1 && (
              <Typography
                component='span'
                variant='caption'
                sx={(theme) => ({ mx: 0.5, color: theme.palette.text.primary })}
              >
                      |
              </Typography>
            )}
          </Fragment>
        ))}
      </Box>
    </StyledFooterCopyRightContainer>
  );
}